import React, { useState, useEffect, useRef } from 'react';
import { motion as Motion, useSpring, useTransform, useMotionValue } from 'framer-motion';

export const MagneticTether = ({ 
  children, 
  pullRadius = 250, 
  tetherRadius = 150, 
  snapThreshold = 350,
  tetherColor = '#FF1E90' // Default Neon Pink 
}) => {
  const ref = useRef(null);
  
  // Motion values for element physics
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Smoother spring: higher damping = less oscillation, lower mass = snappier response
  const springConfig = { damping: 25, stiffness: 200, mass: 0.8 };
  const springX = useSpring(x, springConfig);
  const springY = useSpring(y, springConfig);

  // 3D Tilt calculation based on the spring offsets (reduced range for subtlety)
  const rotateX = useTransform(springY, [-100, 100], [8, -8]);
  const rotateY = useTransform(springX, [-100, 100], [-8, 8]);

  // States for the Tether Line
  const [isTethered, setIsTethered] = useState(false);
  
  // For rendering the SVG line from the element center to the cursor
  const [lineStart, setLineStart] = useState({ x: 0, y: 0 });
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Track tether state in ref to avoid React re-renders on every mouse move
  const tetheredRef = useRef(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!ref.current) return;

      const { clientX, clientY } = e;
      const rect = ref.current.getBoundingClientRect();
      
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const originX = centerX - springX.get();
      const originY = centerY - springY.get();

      const distX = clientX - originX;
      const distY = clientY - originY;
      const distance = Math.sqrt(distX * distX + distY * distY);

      if (distance < pullRadius && distance < snapThreshold) {
        // Apply magnetic pull — update motion values directly (useSpring handles smoothing)
        const pullFactor = 0.35; 
        x.set(distX * pullFactor);
        y.set(distY * pullFactor);

        // Only trigger React re-render when tether state actually changes
        if (distance < tetherRadius && !tetheredRef.current) {
          tetheredRef.current = true;
          setIsTethered(true);
          setMousePos({ x: clientX, y: clientY });
        } else if (distance > tetherRadius + 50 && tetheredRef.current) {
          tetheredRef.current = false;
          setIsTethered(false);
        }
        
        // Update mouse position for SVG tether line only when tethered
        if (tetheredRef.current) {
          setLineStart({ x: centerX, y: centerY });
          setMousePos({ x: clientX, y: clientY });
        }
      } else {
        // Snap back
        x.set(0);
        y.set(0);
        if (tetheredRef.current) {
          tetheredRef.current = false;
          setIsTethered(false);
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [pullRadius, tetherRadius, snapThreshold, springX, springY, x, y]);

  return (
    <>
      <Motion.div
        ref={ref}
        style={{
          x: springX,
          y: springY,
          rotateX,
          rotateY,
          perspective: 1000
        }}
        className="will-change-transform z-20 relative"
      >
        {children}
      </Motion.div>

      {/* The Physical Tether Line Rendering */}
      {isTethered && (
        <svg 
          className="fixed inset-0 pointer-events-none z-30" 
          style={{ width: '100vw', height: '100vh', mixBlendMode: 'difference' }}
        >
          {/* Tension string with some filter for that Electric/Neon vibe */}
          <defs>
            <filter id="neonGlow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          <line 
            x1={lineStart.x}
            y1={lineStart.y}
            x2={mousePos.x} 
            y2={mousePos.y} 
            stroke={tetherColor} 
            strokeWidth="3"
            filter="url(#neonGlow)"
            strokeDasharray="5, 5"
            className="animate-pulse"
          />
          {/* Snap joint at the end of the mouse */}
          <circle 
            cx={mousePos.x} 
            cy={mousePos.y} 
            r="6" 
            fill={tetherColor} 
            filter="url(#neonGlow)"
          />
        </svg>
      )}
    </>
  );
};

export default MagneticTether;
